import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandLogo } from "@/components/BrandLogo";
import { BaseAreaFields, CapabilityPicker } from "@/components/LeadFields";
import { BadgeCheck, Building2, Loader2, ArrowRight, Clock } from "lucide-react";
import { toast } from "sonner";

function SignInPrompt({ next }) {
  const navigate = useNavigate();
  const q = encodeURIComponent(next);
  return (
    <div className="flex flex-col sm:flex-row gap-3" data-testid="claim-signin-prompt">
      <Button onClick={() => navigate(`/auth?mode=signup&role=removalist&next=${q}`)} className="bg-kinetic hover:bg-kinetic-hover text-white" data-testid="claim-signup-btn">Create free removalist account</Button>
      <Button variant="outline" onClick={() => navigate(`/auth?next=${q}`)} data-testid="claim-login-btn">I already have an account</Button>
    </div>
  );
}

function ClaimForm({ listing, token, onDone }) {
  const [v, setV] = useState({
    abn: listing.abn || "", suburb: listing.suburb, state: listing.state, postcode: listing.postcode,
    lat: listing.base_location.coordinates[1], lng: listing.base_location.coordinates[0],
    max_service_radius_km: listing.max_service_radius_km, is_interstate_capable: listing.is_interstate_capable, capabilities: listing.capabilities || [],
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { onDone((await api.post(`/listings/claim/${token}`, v)).data); toast.success("Claim submitted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="claim-form">
      <div className="space-y-1.5 max-w-xs">
        <Label htmlFor="claim-abn">Your ABN</Label>
        <Input id="claim-abn" value={v.abn} onChange={(e) => setV({ ...v, abn: e.target.value })} placeholder="12 345 678 901" inputMode="numeric" data-testid="claim-abn-input" />
        <p className="text-xs text-muted-foreground">We check the ABN before approving your claim.</p>
      </div>
      <BaseAreaFields v={v} set={setV} prefix="claim" />
      <div className="space-y-2"><Label>Services you offer</Label><CapabilityPicker options={listing.capability_options} value={v.capabilities} onChange={(c) => setV({ ...v, capabilities: c })} prefix="claim-cap" /></div>
      <Button onClick={submit} disabled={busy || !v.abn} className="bg-kinetic hover:bg-kinetic-hover text-white gap-2" data-testid="claim-submit-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />} Claim my business
      </Button>
    </div>
  );
}

export default function ClaimListing() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const jobId = params.get("job");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [error, setError] = useState("");
  const [claimed, setClaimed] = useState(null);
  useEffect(() => {
    api.get(`/listings/claim/${token}`, { params: jobId ? { job: jobId } : {} }).then((r) => setListing(r.data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Link not found"));
  }, [token, jobId]);
  const next = `/claim/${token}${jobId ? `?job=${jobId}` : ""}`;
  return (
    <div className="min-h-screen bg-background">
      <header className="max-w-3xl mx-auto px-4 sm:px-6 h-20 flex items-center"><button type="button" onClick={() => navigate("/")}><BrandLogo size="md" /></button></header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pb-20">
        {error && <Card className="p-6" data-testid="claim-error">{error}</Card>}
        {!error && !listing && <Loader2 className="h-6 w-6 animate-spin text-kinetic" />}
        {listing && (
          <Card className="p-6 sm:p-8 space-y-6 animate-fade-up" data-testid="claim-card">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-kinetic mb-2">Claim your free listing</p>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight flex items-center gap-3"><Building2 className="h-8 w-8 text-kinetic shrink-0" /><span data-testid="claim-business-name">{listing.business_name}</span></h1>
              <p className="text-muted-foreground mt-2">{listing.suburb}, {listing.state} {listing.postcode} · {listing.max_service_radius_km} km radius</p>
            </div>
            {claimed ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 space-y-3" data-testid="claim-success">
                <p className="font-bold flex items-center gap-2"><Clock className="h-4 w-4" /> Claim submitted, awaiting MoveTender approval</p>
                <p className="text-sm text-muted-foreground">We'll verify your ABN and email you once approved. You can browse jobs now and quote once approved.</p>
                {jobId && <Button onClick={() => navigate(`/jobs/${jobId}`)} className="gap-2" data-testid="claim-view-lead-btn">View the lead <ArrowRight className="h-4 w-4" /></Button>}
              </div>
            ) : listing.claim_status !== "Unclaimed" && !user ? (
              <div className="space-y-3" data-testid="claim-already">
                <p className="text-sm text-muted-foreground">This business has already been claimed. Log in to manage it.</p>
                <Button variant="outline" onClick={() => navigate("/auth")} data-testid="claim-already-login-btn">Log in</Button>
              </div>
            ) : user === false ? (
              <>
                <p className="text-muted-foreground">Create a free MoveTender removalist account (or log in) to claim this business, verify your ABN, set your service radius and quote on leads.</p>
                <SignInPrompt next={next} />
              </>
            ) : user && user.role !== "removalist" ? (
              <p className="text-sm text-muted-foreground">Log in with a removalist account to claim this business.</p>
            ) : user ? (
              <ClaimForm listing={listing} token={token} onDone={(d) => { setListing({ ...listing, ...d }); setClaimed(d); }} />
            ) : null}
          </Card>
        )}
      </main>
    </div>
  );
}
